import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuditTrailData } from "../util/getAuditTrailData";
import { setupAxiosInterceptor } from "../util/axiosInterceptor";
import { PUBLIC_SEGMENTS } from "../constants";

const isPublicRoute = (path) =>
  PUBLIC_SEGMENTS.some((segment) => path.includes(segment));

// API URLs
const baseUrl = BASE_URL + "auth";
const baseUrlUser = BASE_URL + "user";
const LOGIN_API_URL = `${baseUrl}/login`;
const LOGOUT_API_URL = `${baseUrl}/logout`;
const REGISTER_API_URL = `${baseUrl}/register`;
const UPDATE_API_URL = `${baseUrlUser}/update`;
const CHANGE_PASSWORD_API_URL = `${baseUrlUser}/change-password`;
const PROFILE_API_URL = `${baseUrlUser}/profile`;

// Helper function to set Axios Authorization header
const setAuthToken = () => {
  let token = "";
  if (localStorage.rememberMe === "true") {
    token = localStorage.token;
  } else {
    token = sessionStorage.token;
  }
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
};

// Restore token on page refresh
setAuthToken();

// Setup axios interceptor to handle 401 errors
setupAxiosInterceptor();
// Async Thunk for Logging In
const extractErrorMessage = (error) => {
  const fallback = "Invalid User Name or Password.";
  const responseData = error?.response?.data;

  if (!responseData) {
    return error?.message || fallback;
  }

  if (typeof responseData === "string") {
    try {
      const parsed = JSON.parse(responseData);
      return (
        parsed?.message ||
        parsed?.error ||
        parsed?.errors?.[0]?.message ||
        fallback
      );
    } catch (parseError) {
      return responseData;
    }
  }

  if (typeof responseData === "object" && responseData !== null) {
    return (
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      fallback
    );
  }

  return fallback;
};

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(LOGIN_API_URL, credentials);
      const { token, user } = response.data;

      localStorage.rememberMe = credentials.rememberMe;
      if (credentials.rememberMe) {
        localStorage.token = token;
      } else {
        sessionStorage.token = token;
      }

      // Set token in Axios headers
      setAuthToken();

      return { user, token };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

// Helper function to clear all authentication data
const clearAllAuthData = () => {
  // Clear tokens from both storage locations
  localStorage.removeItem("token");
  sessionStorage.removeItem("token");

  // Clear user data
  localStorage.removeItem("user_id");
  localStorage.removeItem("user_name");
  localStorage.removeItem("loggedinUserInfo");

  // Note: Don't clear rememberMe as it's a user preference

  // Clear axios auth header
  delete axios.defaults.headers.common["Authorization"];

  // Reset the auth token function
  setAuthToken();
};

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (credentials, { rejectWithValue }) => {
    try {
      const audit_trail_data = await getAuditTrailData({
        action: "LOGOUT",
        comment: "Log Out",
        version_content: { action_to_show: "Logged Out" },
      });
      const response = await axios.post(LOGOUT_API_URL, { audit_trail_data });

      // Clear all authentication data
      clearAllAuthData();

      return {};
    } catch (error) {
      // Even if the API call fails, clear local auth data
      clearAllAuthData();
      return rejectWithValue(error.response?.data?.message || "Logout failed");
    }
  }
);

// Async Thunk for Updating User
export const updateUser = createAsyncThunk(
  "auth/updateUser",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.put(UPDATE_API_URL, userData);
      return response.data; // Updated user data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Update failed"
      );
    }
  }
);

// Async Thunk for Changing Password
export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async ({ currentPassword, newPassword }, { rejectWithValue }) => {
    try {
      const response = await axios.put(CHANGE_PASSWORD_API_URL, {
        currentPassword,
        newPassword,
      });

      return response.data.message;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Password change failed"
      );
    }
  }
);

// Async Thunk for Registering User
export const registerUser = createAsyncThunk(
  "auth/registerUser",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(REGISTER_API_URL, userData);
      return response.data; // Success message or user data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Registration failed"
      );
    }
  }
);

// Async Thunk for Fetching Profile
export const fetchProfile = createAsyncThunk(
  "auth/fetchProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(PROFILE_API_URL);

      // Check if response contains "Invalid token" error
      if (response.data?.error === "Invalid token") {
        // Clear auth tokens
        clearAllAuthData();

        // Store redirect path if not already stored
        const currentPath = window.location.pathname;
        const currentSearch = window.location.search || "";
        
        // Extract basename from current path (e.g., /dev, /ri-doc-front, etc.)
        const pathSegments = currentPath.split("/").filter(Boolean);
        const basename = pathSegments.length > 0 ? `/${pathSegments[0]}` : "";

        if (!sessionStorage.getItem("redirectAfterLogin")) {
          // Store the path relative to basename
          let pathToStore = currentPath;
          if (basename && pathToStore.startsWith(basename)) {
            pathToStore = pathToStore.replace(basename, "") || "/";
          }
          if (!pathToStore.startsWith("/")) {
            pathToStore = "/" + pathToStore;
          }
          sessionStorage.setItem("redirectAfterLogin", pathToStore + currentSearch);
          // Also store basename for login redirect
          sessionStorage.setItem("redirectBasename", basename);
        }

        // Determine login path using detected basename
        const loginPath = basename ? `${basename}/login` : "/login";

        // Redirect to login page immediately (skip if on a public route)
        if (!isPublicRoute(currentPath)) {
          window.location.replace(loginPath);
        }

        return rejectWithValue({
          message: "Invalid token",
          status: 401,
          isUnauthorized: true,
          invalidToken: true,
        });
      }

      if (response.data.user.id) {
        localStorage.setItem("user_id", response.data.user.id);
        localStorage.setItem(
          "user_name",
          response.data.user.first_name + " " + response.data.user.last_name
        );
      } else {
        localStorage.removeItem("user_id");
      }

      return response.data.user; // Extract user details
    } catch (error) {
      // If it's a 401 error, the axios interceptor will handle the redirect
      // We still need to reject so the calling code knows the request failed
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to fetch profile";
      const status = error.response?.status;

      // Also check for "Invalid token" in error response
      const isInvalidToken =
        error.response?.data?.error === "Invalid token" ||
        errorMessage === "Invalid token";

      if (isInvalidToken || status === 401) {
        // Clear auth tokens if invalid token detected
        clearAllAuthData();

        // Store redirect path if not already stored
        const currentPath = window.location.pathname;
        const currentSearch = window.location.search || "";
        
        // Extract basename from current path (e.g., /dev, /ri-doc-front, etc.)
        const pathSegments = currentPath.split("/").filter(Boolean);
        const basename = pathSegments.length > 0 ? `/${pathSegments[0]}` : "";

        if (!sessionStorage.getItem("redirectAfterLogin")) {
          // Store the path relative to basename
          let pathToStore = currentPath;
          if (basename && pathToStore.startsWith(basename)) {
            pathToStore = pathToStore.replace(basename, "") || "/";
          }
          if (!pathToStore.startsWith("/")) {
            pathToStore = "/" + pathToStore;
          }
          sessionStorage.setItem("redirectAfterLogin", pathToStore + currentSearch);
          // Also store basename for login redirect
          sessionStorage.setItem("redirectBasename", basename);
        }

        // Determine login path using detected basename
        const loginPath = basename ? `${basename}/login` : "/login";

        // Check if we're already on login page or a public route (no redirect needed)
        const isLoginPage =
          currentPath === "/ri-doc-front/login" ||
          currentPath === "/login" ||
          currentPath.endsWith("/login") ||
          currentPath.includes("/login");

        if (!isLoginPage && !isPublicRoute(currentPath)) {
          // Redirect to login page immediately
          window.location.replace(loginPath);
        }
      }

      return rejectWithValue({
        message: errorMessage,
        status: status,
        isUnauthorized: isInvalidToken || status === 401,
        invalidToken: isInvalidToken,
      });
    }
  }
);

// Helper function to get initial token from storage
const getInitialToken = () => {
  if (localStorage.rememberMe === "true") {
    return localStorage.getItem("token") || null;
  }
  return sessionStorage.getItem("token") || null;
};

// Helper function to check if user appears to be authenticated
// If token exists, assume authenticated initially (will be validated by AuthValidator)
// This prevents blank screen on refresh when user is actually logged in
const getInitialAuthState = () => {
  const token = getInitialToken();
  // If we have a token, assume authenticated initially
  // AuthValidator will validate and update if token is invalid
  return !!token;
};

// Authentication Slice
const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    token: getInitialToken(),
    // If we have token and user_name, assume authenticated initially
    // AuthValidator will validate and update this if token is invalid
    isAuthenticated: getInitialAuthState(),
    // Only validate on mount if we have a token (AuthValidator handles this)
    isValidating: false,
    loading: false,
    error: null,
    successMessage: null,
    showAttentionPopup: false,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isValidating = false;
      state.showAttentionPopup = false;
      // Clear all authentication data from storage
      clearAllAuthData();
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isValidating = false;
      state.showAttentionPopup = false;
      // Clear all authentication data from storage
      clearAllAuthData();
    },
    setValidating: (state, action) => {
      state.isValidating = action.payload;
    },
    setShowAttentionPopup: (state, action) => {
      state.showAttentionPopup = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login Cases
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.isValidating = false;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Logout Cases
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.isValidating = false;
        state.showAttentionPopup = false;
        state.error = null;
        // Note: clearAllAuthData() is called in the thunk, so storage is already cleared
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.isValidating = false;
        state.showAttentionPopup = false;
        state.error = action.payload;
        // Note: clearAllAuthData() is called in the thunk even on error, so storage is already cleared
      })

      // Register Cases
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Registration successful! You can now log in.";
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Change Password Cases
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Password changed successfully!";
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Profile Cases
      .addCase(fetchProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
        // Don't set isValidating here - let AuthValidator control this
        // Only set it if it's already true (meaning validation is in progress)
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.isValidating = false; // Clear validating flag
        state.user = action.payload;
        state.isAuthenticated = true; // Set to true after successful fetch
        state.showAttentionPopup = true;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.loading = false;
        state.isValidating = false; // Clear validating flag
        state.error = action.payload;
        // Only set isAuthenticated to false if it was a 401 error or invalid token
        // This allows components to handle other errors without logging out
        if (action.payload?.isUnauthorized ||
          action.payload?.status === 401 ||
          action.payload?.invalidToken) {
          state.isAuthenticated = false;
          state.user = null;
          state.token = null;
        }
      })

      // Update User Cases
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload; // Update user data in state
        state.successMessage = "User updated successfully!";
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { logout, clearAuth, setValidating, setShowAttentionPopup } = authSlice.actions;
export default authSlice.reducer;
